/**
 * AppSettingsDialog — modal wrapper around AppSettingsView.
 *
 * Replaces the old `/app/:appId/settings` route. Mounted once in `AppLayout`
 * and controlled via the `useSettingsDialogsStore`, so any sidebar or button
 * in the app context can open it without each carrying its own dialog
 * instance.
 *
 * The dialog re-uses the existing AppSettingsView verbatim — it already
 * handles its own loading / error / form / uninstall flow and reads from
 * `useApp()`, which is available wherever AppLayout is mounted.
 */

import { useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { useSettingsDialogsStore } from "@/store/slices/settings-dialogs-slice"
import { AppSettingsView } from "./app-settings-view"

export function AppSettingsDialog() {
  const open = useSettingsDialogsStore((s) => s.appSettings.open)
  const close = useSettingsDialogsStore((s) => s.closeAppSettings)

  /** Reset the global open flag on unmount so an uninstall (which navigates
   *  away from /app/:appId/* and unmounts AppLayout) doesn't leave the
   *  dialog stuck "open" and re-pop on the next app the user visits. */
  useEffect(() => {
    return () => {
      close()
    }
  }, [close])

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        showCloseButton
      >
        {/* The view renders its own SettingsPageHeader, so the DialogTitle
            is for accessibility only. */}
        <DialogTitle className="sr-only">App Settings</DialogTitle>
        <AppSettingsView />
      </DialogContent>
    </Dialog>
  )
}

AppSettingsDialog.displayName = "AppSettingsDialog"
